/**
 * Catalogue & media. Real Main Character pieces, real KL codes, real rupee prices — read from
 * packs/main-character/catalogue/products.jsonl by tools/gen-catalogue.mjs, never invented.
 *
 * The media flags are the point of this page: `sendable` decides whether the agent may attach a
 * photo at all, and one image per product `leads`. Both are honest here — they write through the
 * API. The "what would it send" preview is stubbed visibly, because it needs the real ranking.
 *
 * Author: Danish Khan
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api } from '../lib/api.ts';
import { formatPriceRange } from '../lib/format.ts';
import { mediaUrl } from '../lib/media.ts';
import { canEditMedia } from '../lib/pii.ts';
import { useTenant, useUser } from '../lib/session.tsx';
import { Drawer } from '../components/Drawer.tsx';
import { ErrorState } from '../components/ErrorState.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Gallery } from '../components/Gallery.tsx';
import { Pill } from '../components/Pill.tsx';
import { useToast } from '../components/Toast.tsx';

/** `CATALOGUE_PAGE` in `api/routes/catalogue.ts`. Mirrored, because the wire does not carry it. */
const CATALOGUE_CAP = 300;

const CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'side_table_consoles', label: 'Side tables & consoles' },
  { key: 'chest_cabinet', label: 'Chests & cabinets' },
  { key: 'mirrors', label: 'Mirrors' },
];

export function CataloguePage(): ReactNode {
  const { productId } = useParams();
  const navigate = useNavigate();
  const user = useUser();
  const { agentName } = useTenant();
  // `POST /catalogue/media/:id` is `minRole: manager`. Below that the checkboxes are read-only
  // rather than live-and-then-403: which photos the agent may send is a decision with a name on it.
  const mayEdit = canEditMedia(user.role);
  const { toast, toastError } = useToast();
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('all');

  const products = useQuery({ queryKey: ['products', q, category], queryFn: () => api.products({ q, category }) });
  const detail = useQuery({
    queryKey: ['product', productId],
    queryFn: () => api.product(productId as string),
    enabled: Boolean(productId),
  });

  const flags = useMutation({
    mutationFn: (vars: { mediaId: string; flags: { allowedSend?: boolean; featured?: boolean } }) =>
      api.setMediaFlags(vars.mediaId, vars.flags),
    onSuccess: () => {
      toast('Media updated', { tone: 'ok' });
      void queryClient.invalidateQueries({ queryKey: ['product'] });
      void queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (err) => toastError('update that image', err),
  });

  const rows = useMemo(() => products.data ?? [], [products.data]);

  if (products.isError) {
    return <ErrorState error={products.error} context="load the catalogue" onRetry={() => void products.refetch()} />;
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface px-4 py-2.5">
        {/*
          Title, code and sub-category — what `routes/catalogue.ts` actually matches on. The
          placeholder used to offer a material search, so "marble" returned only the pieces with
          Marble in the TITLE, which looks like a half-broken search rather than a documented limit.
        */}
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search title, KL code or sub-category…"
          aria-label="Search the catalogue"
          className="h-8 w-72 rounded-[var(--h1-radius)] border border-line-strong bg-bg px-2.5 text-sm text-fg"
        />
        <div className="flex gap-1">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setCategory(c.key)}
              className={clsx(
                'rounded-full px-2.5 py-1 text-[11px] font-medium',
                category === c.key ? 'bg-brand-soft text-brand-soft-fg' : 'text-fg-muted hover:bg-surface-2',
              )}
            >
              {c.label}
            </button>
          ))}
        </div>
        {/*
          `GET /catalogue/products` has no cursor — it caps at `CATALOGUE_PAGE` (300) and returns a
          bare array, so there is nothing here to page WITH. At 126 active pieces nothing is cut,
          and the day a pack has more than 300 this line is what says so instead of a grid that
          quietly ends. Adding a cursor is a server change and is reported, not guessed at.
        */}
        <p className="ml-auto text-[11px] text-fg-subtle">
          {rows.length} piece{rows.length === 1 ? '' : 's'}
          {rows.length >= CATALOGUE_CAP && ' — the server caps this list; narrow the search to see the rest'}
        </p>
      </div>

      <div className="h1-scroll min-h-0 flex-1 overflow-y-auto p-4">
        {/*
          A blank white grid is not an answer. Every other list in the console says why it is empty;
          this one rendered nothing at all, both while the request was in flight and when a search
          matched no product — the two states a person most needs told apart.
        */}
        {rows.length === 0 && products.isLoading && (
          <p className="px-1 py-10 text-center text-sm text-fg-muted" role="status" aria-live="polite">
            Loading the catalogue…
          </p>
        )}
        {rows.length === 0 && !products.isLoading && (
          <EmptyState
            title="No products match"
            body="Try a different category, or clear the search to see the whole catalogue."
          />
        )}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          {rows.map((p) => {
            const hero = p.media.find((m) => m.featured) ?? p.media[0];
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => navigate(`/catalogue/${p.id}`)}
                className="h1-card flex flex-col overflow-hidden text-left hover:border-line-strong"
              >
                <span className="block aspect-4/3 overflow-hidden bg-surface-2">
                  {hero && (
                    <img
                      src={mediaUrl(hero)}
                      alt={p.title}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.visibility = 'hidden';
                      }}
                      className="h-full w-full object-cover"
                    />
                  )}
                </span>
                <span className="flex min-h-0 flex-1 flex-col gap-1 p-2.5">
                  <span className="line-clamp-2 text-sm font-medium text-fg">{p.title}</span>
                  <span className="font-mono text-[10px] text-fg-subtle">{p.code}</span>
                  <span className="mt-auto text-sm font-semibold tabular-nums text-fg">
                    {formatPriceRange(p.priceMin, p.priceMax)}
                  </span>
                  <span className="flex flex-wrap gap-1">
                    <Pill tone="neutral">{p.media.length} photos</Pill>
                    {p.media.filter((m) => m.allowedSend).length === 0 && <Pill tone="danger">none sendable</Pill>}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <Drawer
        open={Boolean(productId)}
        onOpenChange={(open) => !open && navigate('/catalogue')}
        title={detail.data?.title ?? 'Loading…'}
        description={detail.data ? `${detail.data.code} · ${formatPriceRange(detail.data.priceMin, detail.data.priceMax)}` : undefined}
        size="lg"
      >
        {detail.isError && <ErrorState error={detail.error} context="load this product" />}
        {detail.data && (
          <div className="flex flex-col gap-5">
            <Gallery
              media={detail.data.media}
              productTitle={detail.data.title}
              {...(mayEdit ? { onToggle: (mediaId: string, f: { allowedSend?: boolean; featured?: boolean }) => flags.mutate({ mediaId, flags: f }) } : {})}
            />
            {!mayEdit && (
              <p className="-mt-3 text-[11px] text-fg-subtle">
                Changing which photos the agent may send is for managers and owners — you are signed in as {user.role}.
              </p>
            )}

            <section>
              <h3 className="mb-1.5 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">Details</h3>
              <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1.5 text-sm">
                <dt className="text-fg-subtle">Dimensions</dt>
                <dd className="text-fg">{detail.data.dims || '—'}</dd>
                <dt className="text-fg-subtle">Materials</dt>
                <dd className="text-fg">{detail.data.materials.join(' · ') || '—'}</dd>
                <dt className="text-fg-subtle">Colours</dt>
                <dd className="text-fg">{detail.data.colours.join(' · ') || '—'}</dd>
                <dt className="text-fg-subtle">Category</dt>
                <dd className="text-fg">{detail.data.subcategory || detail.data.category}</dd>
              </dl>
              <p className="mt-3 text-sm leading-relaxed text-fg-muted">{detail.data.description}</p>
            </section>

            {/*
              Still honest that the preview does not exist, but without our function names and
              route paths in it: this drawer is opened by the client's own team, and
              `retrieve()` / `GET /catalogue/preview` are facts about our source tree, not about
              their catalogue. The reason it is withheld is the part that is theirs to know.
            */}
            <section className="rounded-[var(--h1-radius)] border border-dashed border-line-strong p-3">
              <p className="text-sm font-medium text-fg">“What would {agentName} send?” preview — not built yet</p>
              <p className="mt-1 text-xs text-fg-muted">
                This has to run the same ranking the agent runs when it picks a photo for a customer. A stand-in
                would show you a shortlist the agent would never have chosen, so it is left off until the real
                one can be shown.
              </p>
            </section>
          </div>
        )}
      </Drawer>
    </div>
  );
}
